import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

import type {
  P1Category,
  P1IncidentRaw,
  P1Status,
  P1TimelineMiniResultRaw,
  P1VolumeStatusRaw,
} from '../types';

export const P1_KEY = ['p1'] as const;

// Tela de crise → poll do volume a cada 15s (D10 — WS entra no T8-2).
export function useP1VolumeStatus(): {
  data: P1VolumeStatusRaw | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...P1_KEY, 'volume-status'],
    queryFn: () => api.getP1VolumeStatus(),
    staleTime: 10 * 1000,
    refetchInterval: 15 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

// Lista paginada por cursor (espelha useSlaBreaches). Categoria = query param real (D8).
export function useP1Incidents(filters: { status?: P1Status; category?: P1Category }): {
  rows: P1IncidentRaw[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useInfiniteQuery({
    queryKey: [...P1_KEY, 'incidents', filters.status ?? null, filters.category ?? null],
    queryFn: ({ pageParam }) =>
      api.listP1Incidents({
        ...(filters.status !== undefined ? { status: filters.status } : {}),
        ...(filters.category !== undefined ? { category: filters.category } : {}),
        ...(pageParam != null ? { cursor: pageParam } : {}),
        limit: 50,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 15 * 1000,
  });
  return {
    rows: (query.data?.pages ?? []).flatMap((p) => p.items),
    fetchNextPage: () => void query.fetchNextPage(),
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

// timeline-mini por card — só habilitada p/ level4 (senão null). Hook estável por card montado.
export function useP1TimelineMini(
  id: string,
  enabled: boolean,
): {
  data: P1TimelineMiniResultRaw | null | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...P1_KEY, 'timeline-mini', id],
    enabled,
    queryFn: () => api.getP1TimelineMini(id),
    staleTime: 60 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}
